import { useSelector } from "react-redux";
import { getBusinessById } from "selectors";
import { getIn, useFormikContext } from "formik";
import { withNamespace } from "components/Form/FormikControls";
import { useBoolean } from "hooks";
import {
  OtherContactColumnDefinitions,
  TableRowData,
  UserType,
  UserTypes,
} from "components/Business/BusinessForm/EmailRecipients/GridConfigurations";
import React, { useMemo } from "react";
import { Dialog, DialogContent, DialogTitle } from "components/MaterialDialog";
import { Column } from "components/Layout";
import AgGridTable from "components/AgGrid/AgGridContainer";
import { CollapseTableWrapper } from "components/AgGrid/TableWrapper";
import { AgGridTables } from "constants/aggrid";
import { SlimSecondaryButton } from "components/Button";
import { pluralize } from "lib/pluralize";
import { SmallText } from "components/Text";

interface OtherEmailRecipientsModalProps {
  businessId: string;
  namespace: string;
}

export function OtherEmailRecipientsModal(
  props: OtherEmailRecipientsModalProps,
) {
  const { namespace: ns, businessId } = props;

  const {
    otherEmailRecipients = [],
    businessUsers = [],
    name = "That Business",
  } = useSelector(getBusinessById(businessId)) || {};

  const formikContext = useFormikContext();
  const { setFieldValue, values } = formikContext;

  const [isOpen, open, close] = useBoolean(false);

  function onCopyRecipient(rowData: TableRowData) {
    // Dump these values into the form.
    setFieldValue(withNamespace(ns, "editingValue"), {
      firstName: rowData.firstName,
      lastName: rowData.lastName,
      email: rowData.email,
      phoneNumber: rowData.phoneNumber,
      isAccountsRecipient: rowData.isAccountsRecipient,
      // Business users, vs email recipients... if "either" is true, inject it.
      isCommercialReportRecipient: rowData.isCommercialReportRecipient,
      isComplianceReportRecipient: rowData.isComplianceReportRecipient,
    });
    close();
  }

  const tableData = useMemo((): TableRowData[] => {
    const emailRecipients = getIn(values, withNamespace(ns, "values")) as
      | EmailRecipient[]
      | [];

    const businessUserEmails = businessUsers.map(
      businessUser => businessUser.email,
    );
    const myEmails = emailRecipients.map(
      (emailRecipient: EmailRecipient) => emailRecipient.email,
    );
    const mappedOtherEmailRecipientsExcludingMine = otherEmailRecipients
      .filter(
        (emailRecipient: EmailRecipient) =>
          !myEmails.includes(emailRecipient.email),
      )
      .map((emailRecipient: EmailRecipient) => ({
        ...emailRecipient,
        userType: UserTypes.EMAIL as UserType,
        isMyNousActive: businessUserEmails.includes(emailRecipient.email),
      }));
    const mappedBusinessUsersExcludingMine = businessUsers
      .filter(
        (businessUser: BusinessBusinessUser) =>
          !myEmails.includes(businessUser.email),
      )
      .map((businessUser: BusinessBusinessUser) => ({
        ...businessUser,
        userType: UserTypes.USER as UserType,
        isMyNousActive: true,
        isCommercialReportRecipient: businessUser.isCommercialUser,
        isComplianceReportRecipient: businessUser.isComplianceUser,
      }));

    return [
      ...mappedOtherEmailRecipientsExcludingMine,
      ...mappedBusinessUsersExcludingMine,
    ];
  }, [otherEmailRecipients, businessUsers, values, ns]);

  if (isOpen) {
    return (
      <Dialog open={isOpen} onClose={close} maxWidth={false} fullWidth>
        <DialogTitle onClose={close}>Suggested Contacts</DialogTitle>
        <DialogContent>
          <SmallText>
            These Contacts are being suggested because they are currently being
            sent updates for &quot;{name}&quot; by other AgriNous users (eg.
            Saleyards sending Saleyard Reports, Compliance Reports).
          </SmallText>

          <Column full>
            <AgGridTable
              rowData={tableData || []}
              getRowId={params => params.data.id}
              showGlobalSearchFilter={false}
              context={{
                onCopyRecipient,
              }}
              columnDefs={OtherContactColumnDefinitions}
              WrapperComponent={CollapseTableWrapper}
              hasExportCsvPermissions={false}
              hideSavedViews
              tableName={AgGridTables.BUSINESS_EMAIL_RECIPIENTS_OTHER}
            />
          </Column>
        </DialogContent>
      </Dialog>
    );
  }

  if (tableData && tableData.length > 0) {
    return (
      <SlimSecondaryButton type="button" onClick={open}>
        {tableData.length}&nbsp;Suggested&nbsp;
        {pluralize("Contact", tableData.length)}
      </SlimSecondaryButton>
    );
  }

  return null;
}
